import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Copy, ExternalLink, Flame, Lock, ShieldCheck, Send, Wallet } from "lucide-react";
import { toast } from "sonner";
import { DEV_WALLET, DEV_WALLET_PUMPFUN, DEV_WALLET_SOLSCAN, PUMPFUN_CREATOR_URL, SHORTEN, TELEGRAM_URL, X_URL } from "../config";
import Compliance from "./Compliance";
import Reveal, { EASE } from "./Reveal";

function DevWallet() {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(DEV_WALLET);
        } catch {
            /* clipboard blocked */
        }
        toast.success("DEV WALLET COPIED 🕊️");
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };
    return (
        <div data-testid="dev-wallet-card" className="glass mt-7 flex flex-col gap-3 rounded-2xl border-cyan-400/20 p-4">
            <div className="flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-cyan-300">
                <Wallet className="h-4 w-4" /> DEV ACCOUNT · PUBLIC WALLET
            </div>
            <div className="flex flex-wrap items-center gap-2">
                <span data-testid="dev-wallet-address" className="break-all font-mono text-xs text-slate-200 sm:text-sm">
                    <span className="sm:hidden">{SHORTEN(DEV_WALLET, 10, 10)}</span>
                    <span className="hidden sm:inline">{DEV_WALLET}</span>
                </span>
                <button
                    type="button"
                    onClick={copy}
                    data-testid="dev-wallet-copy-button"
                    className="flex items-center gap-1 rounded-full border border-white/10 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-slate-300 transition-colors hover:border-cyan-400/50 hover:text-cyan-200"
                >
                    {copied ? <Check className="h-3 w-3 text-green-400" /> : <Copy className="h-3 w-3" />} {copied ? "COPIED" : "COPY"}
                </button>
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-1 font-mono text-[10px] font-bold uppercase tracking-widest">
                <a href={DEV_WALLET_SOLSCAN} target="_blank" rel="noopener noreferrer" data-testid="dev-wallet-solscan-link" className="flex items-center gap-1 text-cyan-300 hover:text-cyan-100">
                    SOLSCAN <ExternalLink className="h-3 w-3" />
                </a>
                <a href={DEV_WALLET_PUMPFUN} target="_blank" rel="noopener noreferrer" data-testid="dev-wallet-pumpfun-link" className="flex items-center gap-1 text-cyan-300 hover:text-cyan-100">
                    PUMP.FUN CREATOR REWARDS <ExternalLink className="h-3 w-3" />
                </a>
            </div>
            <p className="font-mono text-[10px] uppercase tracking-wider text-slate-500">
                Receives the Creator Rewards, does the buybacks and the burns. Watch it live — every move is tracked on this site.
            </p>
        </div>
    );
}

const DEV = {
    name: "PIM",
    handle: "BALDPIGEONDev",
    role: "FOUNDER · DEV · CHIEF BALD OFFICER",
    origin:
        "It started with an actual pigeon on Pim's balcony in the Netherlands that was, genuinely, losing its feathers — and refused to leave. A joke in a group chat became a token the same week.",
    quote:
        "I'm not promising you the moon. I'm promising you a bald pigeon, a wallet you can watch, and that I'll keep showing up every single day.",
};

const FACTS = [
    { icon: ShieldCheck, text: "KYC verified & smart contract audited — see below" },
    { icon: ShieldCheck, text: "Creator Rewards split locked on Pump.fun — admin rights revoked" },
    { icon: Lock, text: "Team tokens (26%) locked in Streamflow, verifiable on-chain" },
    { icon: Flame, text: "Buybacks & burns from the dev wallet, listed live on this site" },
];

const LINKS = [
    { label: "DEV ON PUMP.FUN", href: PUMPFUN_CREATOR_URL, testId: "dev-pumpfun-link", primary: true },
    { label: "FOLLOW ON X", href: X_URL, testId: "dev-x-link" },
    { label: "TELEGRAM", href: TELEGRAM_URL, testId: "dev-telegram-link", icon: Send },
];

export default function MeetTheDev() {
    return (
        <section id="dev" data-testid="meet-dev-section" className="relative scroll-mt-24 overflow-hidden py-24 lg:py-32">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_50%_60%_at_80%_40%,rgba(0,240,255,0.07),transparent)]" />
            <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
                <div className="grid items-center gap-14 lg:grid-cols-[1fr_1.05fr]">
                    <Reveal className="relative mx-auto w-full max-w-md lg:max-w-none">
                        <motion.div
                            initial={{ rotate: -3 }}
                            whileInView={{ rotate: -2 }}
                            whileHover={{ rotate: 0, scale: 1.01 }}
                            transition={{ duration: 0.6, ease: EASE }}
                            className="glass overflow-hidden rounded-3xl border-white/15 p-2 shadow-[0_30px_80px_rgba(0,0,0,0.6)]"
                        >
                            <img
                                src="/images/dev/dev-pigeon.webp"
                                alt="Pim, the BALD PIGEON dev, with the bald pigeon mascot"
                                data-testid="dev-main-image"
                                loading="lazy"
                                decoding="async"
                                className="aspect-square w-full rounded-2xl object-cover"
                            />
                        </motion.div>
                        <motion.div
                            initial={{ rotate: 6, y: 20, opacity: 0 }}
                            whileInView={{ rotate: 4, y: 0, opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.25, ease: EASE }}
                            className="glass absolute -bottom-6 -right-2 w-32 overflow-hidden rounded-2xl border-cyan-400/30 p-1.5 shadow-[0_0_40px_rgba(0,240,255,0.25)] sm:-right-6 sm:w-40"
                        >
                            <img
                                src="/images/dev/dev-portrait.webp"
                                alt="Portrait of Pim"
                                data-testid="dev-portrait-image"
                                loading="lazy"
                                decoding="async"
                                className="aspect-[2/3] w-full rounded-xl object-cover"
                            />
                        </motion.div>
                    </Reveal>

                    <div className="lg:pl-6">
                        <Reveal>
                            <span className="kicker">MEET THE DEV</span>
                            <h2 className="headline mt-4 text-4xl text-white sm:text-5xl lg:text-6xl">
                                THE HUMAN BEHIND <span className="text-gradient">THE BIRD</span>
                            </h2>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <div className="mt-6 flex flex-wrap items-baseline gap-x-4 gap-y-1">
                                <span data-testid="dev-name" className="font-display text-3xl uppercase text-cyan-300 md:text-4xl">
                                    {DEV.name}
                                </span>
                                <span className="font-mono text-xs uppercase tracking-widest text-slate-500">@{DEV.handle}</span>
                            </div>
                            <p data-testid="dev-role" className="mt-2 font-mono text-xs font-bold uppercase tracking-[0.25em] text-purple-300">
                                {DEV.role}
                            </p>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <div className="mt-7 space-y-4 text-base leading-relaxed text-slate-300 md:text-lg">
                                <p data-testid="dev-origin">
                                    <span className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-slate-500">ORIGIN STORY — </span>
                                    {DEV.origin}
                                </p>
                                <p>
                                    Not anonymous. Not a faceless team. Just one guy, one very bald pigeon, and a lot of
                                    memes. Everything that matters is on-chain and linked on this page so you never have
                                    to take his word for it.
                                </p>
                                <blockquote data-testid="dev-quote" className="border-l-2 border-cyan-400/60 pl-4 font-bold text-cyan-100">
                                    "{DEV.quote}"
                                    <footer className="mt-1 font-mono text-[10px] font-normal uppercase tracking-widest text-slate-500">— PIM</footer>
                                </blockquote>
                            </div>
                        </Reveal>
                        <Reveal delay={0.2}>
                            <ul data-testid="dev-facts" className="mt-7 flex flex-col gap-3">
                                {FACTS.map((f) => (
                                    <li key={f.text} className="flex items-start gap-3 font-mono text-xs uppercase tracking-wider text-slate-300">
                                        <f.icon className="mt-0.5 h-4 w-4 shrink-0 text-cyan-300" />
                                        {f.text}
                                    </li>
                                ))}
                            </ul>
                        </Reveal>
                        <Reveal delay={0.22}>
                            <DevWallet />
                        </Reveal>
                        <Reveal delay={0.25}>
                            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                                {LINKS.map((l) => (
                                    <a
                                        key={l.label}
                                        href={l.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={l.testId}
                                        className={l.primary ? "btn-primary" : "btn-ghost"}
                                    >
                                        {l.label} {l.icon ? <l.icon className="h-4 w-4" /> : <ExternalLink className="h-4 w-4" />}
                                    </a>
                                ))}
                            </div>
                        </Reveal>
                    </div>
                </div>

                <Reveal delay={0.1} className="mt-20">
                    <div className="glass group relative overflow-hidden rounded-3xl border-white/10">
                        <img
                            src="/images/dev/dev-crowd.webp"
                            alt="BALD PIGEON community artwork — Pim with the Bald Army"
                            data-testid="dev-crowd-image"
                            loading="lazy"
                            decoding="async"
                            className="h-[260px] w-full object-cover object-[center_30%] transition-transform duration-700 group-hover:scale-[1.03] sm:h-[380px] lg:h-[560px]"
                        />
                        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#04060D] via-transparent to-transparent" />
                        <div className="absolute inset-x-0 bottom-0 flex flex-wrap items-end justify-between gap-3 p-5 md:p-8">
                            <div>
                                <span className="kicker">THE BALD ARMY</span>
                                <p className="headline mt-1 text-2xl text-white md:text-4xl">ONE DEV. ONE BIRD. ONE ARMY.</p>
                            </div>
                            <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">COMMUNITY ARTWORK</span>
                        </div>
                    </div>
                </Reveal>

                <Compliance />
            </div>
        </section>
    );
}
